import { useEffect, useRef, useState } from 'react';
import { ARC_EASING, entranceArc } from '@/components/brand/coreFlight';
import { KanameMark } from '@/components/brand/KanameMark';
import { CORE_R, MARK_HALF } from '@/components/brand/geometry';
import { cn } from '@/components/ui';
import type { EntranceFrom } from './entrance';

/*
  ShellEntrance — вторая ключевая анимация (docs/brand-concept.md §5): из пространства входа в
  рабочее место. Ядро — один и тот же объект на всём пути.

  1. Знак стоит там же, где был на странице входа.
  2. Веер складывается: последний лепесток уходит первым, каждый прячется за соседа.
  3. Ядро по дуге летит в свой дом в главном меню: разгоняется, чуть вытягивается по ходу,
     замедляется и уменьшается до размера ядра в знаке меню.
  4. Тёмная сцена растворяется, рабочее место собирается: меню, пункты, страница.
  5. Ядро садится — веер знака в меню раскрывается вокруг него.
  Меньше движения в настройках системы — сразу рабочее место, без перехода.

  Темп (pace):
  - scene — после страницы входа: неспешно, рабочее место собирается следом (вызывающий проигрывает
    enter-* у меню и холста);
  - brisk — после сцены первой загрузки (BootScene, R42): оболочка уже стоит под сценой и принимает клики,
    поэтому всё сразу и коротко — сцена гаснет с 120-й мс, ядро долетает за 520 мс; знак добавляет к
    загрузке не больше 0,6 с.
  contained — слой внутри рамки, а не на весь экран (плитка галереи, повтор первой загрузки в «Основе»); дом
  ядра ищется в той же рамке.
*/

export type EntrancePace = 'scene' | 'brisk';

interface ShellEntranceProps {
  /** Где стоял знак: центр и сторона относительно слоя (на весь экран — относительно окна). */
  from: EntranceFrom;
  /** Ядро село в дом: можно раскрыть веер знака в меню. */
  onLand: () => void;
  /** Переход закончен: сцену можно убрать. */
  onDone: () => void;
  pace?: EntrancePace;
  contained?: boolean;
}

/** Расписание перехода, мс от начала: сложить веер, растворить сцену, лететь, закончить. */
const PACES: Record<
  EntrancePace,
  { foldAt: number; revealAt: number; flyAt: number; flyMs: number; doneAt: number }
> = {
  scene: { foldAt: 160, revealAt: 760, flyAt: 560, flyMs: 820, doneAt: 560 + 820 + 260 },
  brisk: { foldAt: 0, revealAt: 120, flyAt: 80, flyMs: 520, doneAt: 760 },
};

/** Доля ядра в стороне знака. */
const CORE_SHARE = (CORE_R * 2) / (MARK_HALF * 2);

export function ShellEntrance({
  from,
  onLand,
  onDone,
  pace = 'scene',
  contained = false,
}: ShellEntranceProps) {
  const layerRef = useRef<HTMLDivElement>(null);
  const coreRef = useRef<HTMLDivElement>(null);
  const [folded, setFolded] = useState(false);
  const [reveal, setReveal] = useState(false);
  const [landed, setLanded] = useState(false);
  const coreSize = from.size * CORE_SHARE;
  // Переход разыгрывается один раз на точку входа, с её исходными параметрами.
  const entry = useRef({ from, onLand, onDone, pace, contained });

  useEffect(() => {
    const initial = entry.current;
    const t = PACES[initial.pace];
    const initialCoreSize = initial.from.size * CORE_SHARE;
    // Полёт снимается вместе с переходом: ушли раньше (повтор первой загрузки) — onLand из него уже не придёт,
    // и веер в меню не раскроется под новой сценой.
    let flight: Animation | undefined;
    const timers = [
      window.setTimeout(() => setFolded(true), t.foldAt),
      window.setTimeout(() => setReveal(true), t.revealAt),
      window.setTimeout(() => fly(), t.flyAt),
      // Страховка: если полёт не случился (меню скрыто, анимация прервана), веер в меню всё равно
      // раскрывается, а сцена уходит.
      window.setTimeout(() => {
        initial.onLand();
        initial.onDone();
      }, t.doneAt),
    ];
    function fly() {
      const core = coreRef.current;
      const layer = layerRef.current;
      const scope = initial.contained ? layer?.parentElement : document;
      const home = scope?.querySelector<HTMLElement>('[data-brand-home]');
      if (!core || !layer || !home) return;
      const o = layer.getBoundingClientRect();
      const h = home.getBoundingClientRect();
      // Ядро в знаке меню: знак 40 px внутри гнезда 48 px.
      const targetMark = Math.min(h.width, h.height) * (40 / 48);
      const scale = (targetMark * CORE_SHARE) / initialCoreSize;
      const dx = h.left - o.left + h.width / 2 - initial.from.x;
      const dy = h.top - o.top + h.height / 2 - initial.from.y;
      // Дуга с вытяжкой по ходу — brand/coreFlight.ts.
      flight = core.animate(entranceArc(dx, dy, scale), {
        duration: t.flyMs,
        easing: ARC_EASING,
        fill: 'forwards',
      });
      flight.onfinish = () => {
        setLanded(true);
        initial.onLand();
      };
    }
    return () => {
      timers.forEach((id) => window.clearTimeout(id));
      flight?.cancel();
    };
  }, []);

  return (
    <div
      ref={layerRef}
      aria-hidden
      className={cn('pointer-events-none inset-0 z-overlay', contained ? 'absolute' : 'fixed')}
    >
      <div
        className={cn(
          'absolute inset-0 bg-night transition-opacity',
          pace === 'brisk' ? 'duration-smooth' : 'duration-slow',
          reveal && 'opacity-0',
        )}
      />
      {/* Короткий темп: веер складывается вместе со сценой и гаснет с ней — над собранной оболочкой
          лепестки не остаются. */}
      <div
        className={cn(
          'absolute',
          pace === 'brisk' && 'transition-opacity duration-smooth',
          pace === 'brisk' && reveal && 'opacity-0',
        )}
        style={{
          left: from.x - from.size / 2,
          top: from.y - from.size / 2,
          width: from.size,
          height: from.size,
        }}
      >
        <KanameMark open={!folded} core="none" variant={from.variant} className="size-full" />
      </div>
      <div
        ref={coreRef}
        className={cn('absolute rounded-full core-sphere', landed && 'opacity-0')}
        style={{ left: from.x - coreSize / 2, top: from.y - coreSize / 2, width: coreSize, height: coreSize }}
      />
    </div>
  );
}
